import { useState, useEffect } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import NewProtectorShareReceiptModal from '../../components/modals/NewProtectorShareReceiptModal';
import { getProtectorShareReceipts } from '../../services/GuaranteesService';

interface ShareReceipt {
  id: number;
  partner_name: string;
  quantity: number;
  minute_date: string;
}

interface ProtectorDetailsShareReceiptsSectionProps {
  protectorId?: string;
}

const ProtectorDetailsShareReceiptsSection = ({ 
  protectorId 
}: ProtectorDetailsShareReceiptsSectionProps) => {
  const [shareReceipts, setShareReceipts] = useState<ShareReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (protectorId) {
      fetchShareReceipts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protectorId, pagination.page]);

  const fetchShareReceipts = async () => {
    if (!protectorId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getProtectorShareReceipts(protectorId, pagination.page, pagination.size);
      if (response.success && 'data' in response) {
        setShareReceipts(response.data.content || []);
        setPagination({
          page: response.data.page || 0,
          size: response.data.size || 10,
          totalElements: response.data.totalElements || 0,
          totalPages: response.data.totalPages || 0
        });
      } else {
        setError('Error al cargar las cesiones de acciones');
      }
    } catch (err) {
      setError('Error al cargar las cesiones de acciones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    fetchShareReceipts();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const columns = [
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Socio</P>,
      selector: (row: ShareReceipt) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.partner_name || '-'}
        </P>
      ),
      minWidth: '200px',
      grow: 2
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Cantidad</P>,
      selector: (row: ShareReceipt) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.quantity || '-'}
        </P>
      ),
      minWidth: '100px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de la acta</P>,
      selector: (row: ShareReceipt) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatDate(row.minute_date)}
        </P>
      ),
      minWidth: '140px',
      grow: 1
    }
  ];

  return (
    <>
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          Cesiones de acciones
        </H>
        <Button variant='secondary' icon='plus_icon' iconAlign='left' onClick={handleOpenModal}>
          Nueva cesión de acciones
        </Button>
      </Card>
      
      {loading ? (
        <Card>
          <TableSkeleton rows={5} columns={3} />
        </Card>
      ) : error ? (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            {error}
          </P>
        </Card>
      ) : shareReceipts && shareReceipts.length > 0 ? (
        <Card>
          <Table
            columns={columns}
            data={shareReceipts}
            tableData={pagination}
            handleChangePage={handleChangePage}
          />
        </Card>
      ) : (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            No hay cesiones de acciones para mostrar.
          </P>
        </Card>
      )}

      <NewProtectorShareReceiptModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        protectorId={protectorId}
      />
    </>
  );
};

export default ProtectorDetailsShareReceiptsSection;

